// OwnerModeContext — single source of truth for the owner's product-mode toggle
// (auto / mortgage / real_estate / dual_licensed).
//
// - Persists to localStorage under `owner_mode`
// - Reacts instantly across every component in the same tab (React context)
// - Syncs across browser tabs via the `storage` window event
// - Exposes `effectiveProductType`: the value pages should actually filter by
//   (owner override wins; else tenant.product_type; else 'mortgage')
// - Exposes `hasProduct(p)`: the only sanctioned way to ask "is this tenant
//   licensed for mortgage / real_estate?" — a dual_licensed tenant has both.
//   Never string-compare `effectiveProductType` in new code.
import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'owner_mode';
export const DUAL_LICENSED = 'dual_licensed';
export const PRODUCT_TYPES = ['mortgage', 'real_estate', DUAL_LICENSED];
export const OWNER_MODES = ['auto', ...PRODUCT_TYPES];

// Pure — shared by the provider and the no-provider fallback.
export const productTypeHas = (productType, product) =>
  productType === product || productType === DUAL_LICENSED;

const OwnerModeCtx = createContext(null);

const readMode = () => {
  if (typeof localStorage === 'undefined') return 'auto';
  return localStorage.getItem(STORAGE_KEY) || 'auto';
};

export function OwnerModeProvider({ children }) {
  const { user } = useAuth();
  const isOwner = !!user?.is_super_admin; // platform-owner flag (ticket 54), not an email match

  const [ownerMode, setOwnerModeState] = useState(readMode);
  const [tenantProductType, setTenantProductType] = useState('mortgage'); // updated by any consumer that fetches /tenants/mine

  // Cross-tab sync: when another tab writes to `owner_mode`, pull it in here.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === STORAGE_KEY) {
        setOwnerModeState(e.newValue || 'auto');
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const setOwnerMode = useCallback((m) => {
    setOwnerModeState(m);
    if (m === 'auto') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, m);
    // Kick a synthetic storage event so listeners in OTHER tabs re-sync.
    // (In the current tab, React state already updated.)
    try {
      window.dispatchEvent(new StorageEvent('storage', {
        key: STORAGE_KEY,
        newValue: m === 'auto' ? null : m,
      }));
    } catch { /* Safari older versions */ }
  }, []);

  const effectiveProductType = useMemo(() => {
    if (isOwner && ownerMode !== 'auto') return ownerMode;
    return tenantProductType || 'mortgage';
  }, [isOwner, ownerMode, tenantProductType]);

  const hasProduct = useCallback((p) => productTypeHas(effectiveProductType, p), [effectiveProductType]);

  const value = useMemo(() => ({
    ownerMode,
    setOwnerMode,
    isOwner,
    effectiveProductType,
    hasProduct, // hasProduct('mortgage' | 'real_estate') — true for dual_licensed too
    tenantProductType,
    setTenantProductType, // consumers that fetch /tenants/mine call this once loaded
  }), [ownerMode, setOwnerMode, isOwner, effectiveProductType, hasProduct, tenantProductType]);

  return <OwnerModeCtx.Provider value={value}>{children}</OwnerModeCtx.Provider>;
}

export function useOwnerMode() {
  const ctx = useContext(OwnerModeCtx);
  if (!ctx) {
    // Fallback for components that render outside the provider (e.g., login page).
    return {
      ownerMode: 'auto',
      setOwnerMode: () => {},
      isOwner: false,
      effectiveProductType: 'mortgage',
      hasProduct: (p) => productTypeHas('mortgage', p),
      tenantProductType: 'mortgage',
      setTenantProductType: () => {},
    };
  }
  return ctx;
}
